/**
 * RYFE session + branding context. Holds the authenticated user, their active
 * workspace, and the runtime branding; exposes auth actions. Kept deliberately
 * small — the heavy editor state lives in Excalidraw itself.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { ryfeApi } from "./api";
import { applyBranding, DEFAULT_BRANDING, fetchBranding } from "./branding";

import type { Branding, RyfeGroup, RyfeUser, RyfeWorkspace } from "./api";
import type { ReactNode } from "react";

export type SessionStatus = "loading" | "anon" | "authed";

export interface RyfeContextValue {
  status: SessionStatus;
  user: RyfeUser | null;
  workspaces: RyfeWorkspace[];
  workspace: RyfeWorkspace | null;
  // Redesign Welle R — der persönliche „Privatbereich" ist der bei Registrierung
  // angelegte Heim-Workspace (ältester Beitritt → `workspaces[0]`, deterministisch
  // über `m.created_at` sortiert). Er wird NICHT in der Workspace-Liste geführt,
  // sondern nur über den „Privatbereich"-Scope betreten; `regularWorkspaces` ist
  // die Dropdown-Liste ohne ihn. `privateScope` = der Privatbereich ist aktiv,
  // dann ist im Dropdown kein Workspace ausgewählt.
  privateWorkspace: RyfeWorkspace | null;
  regularWorkspaces: RyfeWorkspace[];
  privateScope: boolean;
  enterPrivate: () => void;
  branding: Branding;
  setWorkspace: (ws: RyfeWorkspace) => void;
  // v1.8 WS-Plattform (D-112/D-132) — the active scope is the workspace, with an
  // optional group sub-scope layered on top. `group=null` means workspace level.
  groups: RyfeGroup[];
  group: RyfeGroup | null;
  setGroup: (g: RyfeGroup | null) => void;
  reloadGroups: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const RyfeContext = createContext<RyfeContextValue | null>(null);

export const useRyfe = (): RyfeContextValue => {
  const ctx = useContext(RyfeContext);
  if (!ctx) {
    throw new Error("useRyfe must be used within <RyfeProvider>");
  }
  return ctx;
};

export const RyfeProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<RyfeUser | null>(null);
  const [workspaces, setWorkspaces] = useState<RyfeWorkspace[]>([]);
  const [workspace, setWorkspaceState] = useState<RyfeWorkspace | null>(null);
  const [groups, setGroups] = useState<RyfeGroup[]>([]);
  const [group, setGroupState] = useState<RyfeGroup | null>(null);
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  // Der Privatbereich ist beim Start aktiv (der Heim-Workspace ist der Default).
  const [privateScope, setPrivateScope] = useState<boolean>(true);

  // Heim-Workspace = ältester Beitritt (workspaces[0]); die Dropdown-Liste zeigt
  // nur die „echten" Workspaces darüber hinaus.
  const privateWorkspace = workspaces[0] ?? null;
  const regularWorkspaces = privateWorkspace
    ? workspaces.filter((w) => w.id !== privateWorkspace.id)
    : workspaces;

  const refresh = useCallback(async () => {
    try {
      const { user: u, workspaces: ws } = await ryfeApi.me();
      setUser(u);
      setWorkspaces(ws);
      setWorkspaceState((prev) => prev ?? ws[0] ?? null);
      setStatus("authed");
    } catch {
      setUser(null);
      setWorkspaces([]);
      setWorkspaceState(null);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    fetchBranding().then((b) => {
      setBranding(b);
      applyBranding(b);
    });
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      await ryfeApi.login(email, password);
      await refresh();
    },
    [refresh],
  );

  const register = useCallback(
    async (email: string, password: string) => {
      await ryfeApi.register(email, password);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await ryfeApi.logout();
    setUser(null);
    setWorkspaces([]);
    setWorkspaceState(null);
    setGroups([]);
    setGroupState(null);
    setStatus("anon");
  }, []);

  // Switching the workspace drops the active group sub-scope (a group belongs to
  // exactly one workspace) and lets the effect below reload the new WS's groups.
  // Ein Wechsel auf einen ECHTEN Workspace verlässt den Privatbereich; ein
  // Wechsel (zurück) auf den Heim-Workspace ist der Privatbereich.
  const setWorkspace = useCallback(
    (ws: RyfeWorkspace) => {
      setWorkspaceState((prev) => {
        if (prev && prev.id !== ws.id) {
          setGroupState(null);
        }
        return ws;
      });
      setPrivateScope(!!privateWorkspace && ws.id === privateWorkspace.id);
    },
    [privateWorkspace],
  );

  // „Privatbereich" betreten: echter Switch auf den Heim-Workspace, Gruppe fällt
  // weg, Dropdown leert sich (privateScope=true). Ohne Heim-WS ein No-op.
  const enterPrivate = useCallback(() => {
    if (!privateWorkspace) {
      return;
    }
    setWorkspaceState(privateWorkspace);
    setGroupState(null);
    setPrivateScope(true);
  }, [privateWorkspace]);

  const setGroup = useCallback((g: RyfeGroup | null) => {
    setGroupState(g);
  }, []);

  const reloadGroups = useCallback(async () => {
    if (!workspace) {
      setGroups([]);
      return;
    }
    try {
      const { groups: gs } = await ryfeApi.listGroups(workspace.id);
      setGroups(gs);
    } catch {
      setGroups([]);
    }
  }, [workspace]);

  // Load the active workspace's groups whenever it changes.
  useEffect(() => {
    reloadGroups();
  }, [reloadGroups]);

  return (
    <RyfeContext.Provider
      value={{
        status,
        user,
        workspaces,
        workspace,
        privateWorkspace,
        regularWorkspaces,
        privateScope,
        enterPrivate,
        branding,
        setWorkspace,
        groups,
        group,
        setGroup,
        reloadGroups,
        login,
        register,
        logout,
        refresh,
      }}
    >
      {children}
    </RyfeContext.Provider>
  );
};
